import { ref } from 'vue'
import { defineStore } from 'pinia'
import referenceapi from "@/api/referenceapi"
import { createDosageList, createExistingMedicList, createSubstanceReactionList, createSubstanceList, createMultiSubstanceList, createMedicLuList } from "@/functions/medicReference"
import { AggressorFilter } from '@/models/folerpatient.model'
import { ApiError, ConceptGroupResponse, FolderPatientApi } from '@/apiGenerate/medserver'
import { useI18n } from 'vue-i18n'
import { useToast } from 'primevue'
import { medServerApiConfig } from '@/config/medserver-api.config'
import { toastError } from '@/utils/toast-utils'

export const useReferenceStore = defineStore('Reference', () => {

  const {t} = useI18n();
  const toast = useToast();
  const folderPatientApi = new FolderPatientApi(medServerApiConfig);

  const administrationRouteReference = ref(null)
  async function setAdministrationRouteReference() {
    administrationRouteReference.value = await referenceapi.getRefAdministrationRoutes();
  }

  const medicReference = ref(null)
  async function setMedicReference() {
    const data = await referenceapi.getRefMedication();
    medicReference.value = data
  }

  const medicLuReference = ref(null)
  async function setMedicLuReference() {
    const data = await referenceapi.getRefLuMedication();
    medicLuReference.value = data
  }

  const dosageLuReference = ref(null)
  async function setDosageLuReference() {
    const data = await referenceapi.getRefLuDosage();
    dosageLuReference.value = data
  }

  async function getAllDosageAndMedicReferences() {
    const dosageLuRefPromise = !dosageLuReference.value ? setDosageLuReference() : Promise.resolve()
    const medicRefPromise = !medicReference.value ? setMedicReference() : Promise.resolve()
    const medicLuRefPromise = !medicLuReference.value ? setMedicLuReference() : Promise.resolve()
    await Promise.all([dosageLuRefPromise, medicRefPromise, medicLuRefPromise])    
  }

  // Computed lists
  const dosageList = ref(null)
  const existingMedicList = ref([])
  const substanceReactionList = ref([])
  const substanceFullList = ref([])
  const medicLuFullList = ref([])
  const multiSubstanceList = ref([])
  
  async function createMedicSearchReferences(allExpressions: any, forceReload:boolean, patientId: string )  {
    if (!forceReload && multiSubstanceList.value.length > 0) return;
    //Reset
    existingMedicList.value = []
    substanceReactionList.value = []
    dosageList.value = null
    substanceFullList.value = []
    multiSubstanceList.value = []
    medicLuFullList.value = []
    //Get data from backend
    getAllDosageAndMedicReferences()
    if (!dosageLuReference.value || !medicReference.value || !medicLuReference.value) return;
    // A1 - existingMedicList
    let sapMedicsConcepts: ConceptGroupResponse[] = [];
  
    await folderPatientApi.getConceptReferentialByPatient({patientId: patientId, aggressorFilter: AggressorFilter.MedicSubstance})
        .then((result: ConceptGroupResponse[]) => {
          sapMedicsConcepts = result;
        }).catch((error: ApiError) => {
            toastError(toast, {
                summary: t('common.toast.error'),
                detail: error.status ? t(`error.${error.status}`) : t('error.DEFAULT')
            });
        });
    if (!sapMedicsConcepts) return;
    existingMedicList.value = createExistingMedicList(sapMedicsConcepts);
    // A2 - existingMedicList
    if (!allExpressions) return;
    substanceReactionList.value = createSubstanceReactionList(allExpressions)  
    
    // B - dosageList
    dosageList.value = createDosageList(dosageLuReference.value)
    // 1 - substanceFullList (=> from medicReference)
    substanceFullList.value = createSubstanceList(medicReference.value, dosageList.value, existingMedicList.value, substanceReactionList.value)
    // 2- medicLuFullList (=> from medicLuReference)
    medicLuFullList.value = createMedicLuList(medicLuReference.value, dosageList.value, existingMedicList.value, substanceReactionList.value)
    // 3 - multiSubstanceList (=> from substanceFullList & medicLuFullList)
    multiSubstanceList.value = createMultiSubstanceList(medicLuFullList.value, substanceFullList.value, dosageList.value, existingMedicList.value, substanceReactionList.value)
  }
  function getMedicReference(sctSubstanceKey: string) {
    if (!sctSubstanceKey) return null;
    if (!dosageList.value) return null;
    return dosageList.value[sctSubstanceKey]
  }


  //Return
  return {
    existingMedicList,
    substanceReactionList,
    dosageList,
    substanceFullList,
    medicLuFullList,
    multiSubstanceList,
    createMedicSearchReferences,
    getMedicReference,

    medicLuReference,
    setMedicLuReference,
    medicReference,
    setMedicReference,
    dosageLuReference,
    setDosageLuReference,
    getAllDosageAndMedicReferences,

    administrationRouteReference,
    setAdministrationRouteReference,
  }
})
